import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import Icon from '../Icon';
import SearchBarDropdown from './SearchBarDropdown';
import style from './SearchBar.module.less';
import useGlobalToggle from '../../hooks/useGlobalToggle';
import clsx from 'clsx';
import dynamic from 'next/dynamic';

const AutoSuggestWrapper = dynamic(() => import('./AutoSuggestWrapper'), {
  ssr: false,
});

/**
 * @typedef SearchSuggestion
 * @property {string} term The value to use as a search term
 * @property {string} display The human-friendly display value
 */

function SearchBar({
  placeholder = 'Search entire library',
  cid = '',
  onSubmit = () => {},
  showDropdown = false,
  className,
}) {
  const [value, setValue] = useState('');

  /**
   * Submit the search term.
   *
   * @param {string} term
   * @param {string} [contentType]
   * @param {string} [grade]
   */
  const doSubmit = (term, contentType, grade) => {
    Promise.resolve(onSubmit(term, contentType, grade)).then(() => {
      setValue('');
      setContentType('resources');
      setGrade('');
      setIsOpen(false);
    });
  };

  /**
   * Handle a change in the value of the search input.
   *
   * @param {Event} event
   * @param {Object} args
   * @param {string} args.newValue The new input value
   */
  const onChange = (event, { newValue }) => {
    event.preventDefault();
    setValue(newValue);
  };

  /**
   * Handle the submission of the encompassing form.
   *
   * @param {Event} event
   */
  const handleSubmit = event => {
    event.preventDefault();
    doSubmit(value, contentType, grade);
  };

  /**
   * Handle the request to clear the search term.
   */
  const handleClear = () => {
    setValue('');
  };

  const rootRef = useRef(null);
  const [isOpen, setIsOpen] = useGlobalToggle(rootRef);
  const [contentType, setContentType] = useState('resources');
  const [grade, setGrade] = useState('');
  const handleFocusSearchBar = () => {
    setIsOpen(true);
  };

  return (
    <div
      className={clsx(
        style.container,
        {
          [style['with-dropdown']]: showDropdown,
        },
        className
      )}
      ref={rootRef}
    >
      <div
        className={clsx(style['search-bar-container'], {
          [style['with-dropdown']]: showDropdown,
        })}
      >
        <div className={style['search-bar']} onFocus={handleFocusSearchBar}>
          <div className={clsx(style['search-form'], 'form')}>
            <div className={clsx(style['search-box'])}>
              <form
                action="/resources/"
                method="get"
                title="Search 36,000 educational resources"
                onSubmit={handleSubmit}
                role="search"
              >
                {cid.length ? (
                  <input type="hidden" name="cid" value="11.74" />
                ) : null}
                <AutoSuggestWrapper
                  doSubmit={doSubmit}
                  value={value}
                  searchInputClass={style['search-input']}
                  placeholder={placeholder}
                  onChange={onChange}
                />
                <Button
                  title="Search"
                  type="submit"
                  className={clsx(style['search-button'], style.btn)}
                  color="tertiary"
                  shadowless={true}
                >
                  <Icon name="search" />
                </Button>
                {value.length > 0 ? (
                  <Button
                    title="Clear"
                    className={clsx(style['search-button-clear'], style.btn)}
                    color="tertiary"
                    shadowless={true}
                    onClick={handleClear}
                  >
                    <Icon name="cancel-circled" />
                  </Button>
                ) : null}
              </form>
            </div>
          </div>
        </div>
      </div>
      {showDropdown && (
        <SearchBarDropdown
          isOpen={isOpen}
          contentType={contentType}
          setContentType={setContentType}
          grade={grade}
          setGrade={setGrade}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}

SearchBar.propTypes = {
  placeholder: PropTypes.string,
  cid: PropTypes.string,
  onSubmit: PropTypes.func,
  showDropdown: PropTypes.bool,
  className: PropTypes.string,
};

export default SearchBar;
